import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, LayoutDashboard } from 'lucide-react';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ─────────────────────────────────────────────────────────────────────────────
  The shell every operational screen sits in.

  Before this, each of the nine pages built its own header: an <h1>, a
  paragraph, and a row of buttons, with the spacing and type size chosen again
  every time. That is the single biggest reason the dashboard read as nine
  screens rather than one product.

  TWO DECISIONS WORTH THE COMMENT

  1. The header and the toolbar are ONE card, not two.
     Stacking a header card above a filter card puts two horizontal rules and
     a gap above the first row of data. On the 1366×768 laptops school offices
     actually run, that is about an extra table row's worth of chrome on every
     screen — on a product whose users read these tables all day.

  2. The current breadcrumb is not painted in the brand colour.
     It is the one crumb that is NOT a link, so colouring it like the links
     beside it teaches people to click a dead word. Weight marks position;
     colour marks what is clickable.
  ─────────────────────────────────────────────────────────────────────────────
*/

export interface Crumb {
  label: string;
  href?: string;
}

export interface PageLayoutProps {
  title: string;
  /** One line saying what this screen is. Survives on a phone — on several
   *  screens it is the part that says what you are looking at. */
  subtitle?: string;
  breadcrumbs?: Crumb[];
  /** Primary actions for the screen. Right-aligned, beside the title. */
  actions?: ReactNode;
  /** Search and filters — things you do TO this page, so they belong to its
   *  header rather than to a card of their own. */
  toolbar?: ReactNode;
  /** Context that qualifies the whole screen rather than any one row: the
   *  as-at time of the figures, the institution, whether the gate is live.
   *  Sits with the title so it is read before the data, not after. */
  meta?: ReactNode;
  /** Set false when the page lays out its own cards (a dashboard grid) rather
   *  than filling one content panel (a table). */
  contained?: boolean;
  children: ReactNode;
}

export function PageLayout({
  title,
  subtitle,
  breadcrumbs = [],
  actions,
  toolbar,
  meta,
  contained = true,
  children,
}: PageLayoutProps) {
  return (
    <div className="flex min-h-full flex-col gap-3">
      {/* ── Header card, with the toolbar as its lower half ─────────────── */}
      <div className={styles.panel}>
        <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            {breadcrumbs.length > 0 && (
              <nav aria-label="Breadcrumb" className="mb-1.5 flex flex-wrap items-center gap-1">
                <Link
                  to="/dashboard"
                  aria-label="Live board"
                  className="flex items-center text-muted-foreground transition-colors hover:text-primary"
                >
                  <LayoutDashboard size={12} />
                </Link>
                {breadcrumbs.map((crumb, i) => {
                  const isLast = i === breadcrumbs.length - 1;
                  return (
                    <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
                      <ChevronRight size={11} className="flex-shrink-0 text-muted-foreground/60" />
                      {isLast || !crumb.href ? (
                        <span
                          aria-current={isLast ? 'page' : undefined}
                          className={cn(
                            'truncate text-[11px]',
                            isLast ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground',
                          )}
                        >
                          {crumb.label}
                        </span>
                      ) : (
                        <Link
                          to={crumb.href}
                          className="truncate text-[11px] font-medium text-muted-foreground transition-colors hover:text-primary"
                        >
                          {crumb.label}
                        </Link>
                      )}
                    </span>
                  );
                })}
              </nav>
            )}

            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
              <h1 className={styles.pageTitle}>{title}</h1>
              {subtitle && <span className={styles.meta}>{subtitle}</span>}
            </div>

            {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
          </div>

          {actions && (
            <div className="flex flex-shrink-0 items-center gap-2 sm:pt-4">{actions}</div>
          )}
        </div>

        {toolbar && (
          <div className={cn(styles.panelRow, 'flex flex-wrap items-center gap-2.5')}>{toolbar}</div>
        )}
      </div>

      {/*
        `overflow-clip`, not `overflow-hidden`. Both clip children to the 4px
        corners, which is all this needs — but `hidden` also makes the card a
        scrolling container, and nothing inside a scrolling container can stick
        to the viewport. That would quietly cancel the sticky table head.
      */}
      {contained ? (
        <div className={cn(styles.panel, 'flex-1 overflow-clip')}>{children}</div>
      ) : (
        <div className="flex-1">{children}</div>
      )}
    </div>
  );
}
